import {
    Backdrop,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { withRouter } from 'react-router-dom';
import axios from '../axios-default';
import LoadingSpinner from '../components/Loader/LoadingSpinner';
import { openModal } from '../store/Global/global';
import { easySnackbar } from '../store/Noti/noti.actions';
import { useLang } from '../util/lang';
import { parseStages } from '../util/selfDesignSets';
import { localColorTheme } from '../components/Modal/CustomModal';

const useStyles = makeStyles(() => ({
    root: {
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: '#000000',
    },
    video: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        background: '#000000',
        display: 'block',
    },
    actions: {
        position: 'absolute',
        top: 16,
        right: 16,
        zIndex: 2,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    actionButton: {
        background: '#4FC3F7',
        color: '#FFFFFF',
        fontWeight: 600,
        fontSize: 14,
        lineHeight: 1.4,
        padding: '8px 20px',
        borderRadius: 4,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.28)',
        '&:hover': {
            background: '#29B6F6',
        },
    },
    summaryRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '6px 0',
    },
    summaryLabel: {
        fontSize: 14,
        color: '#333',
        '&::after': {
            content: '": "',
        },
    },
    summaryValue: {
        fontSize: 14,
        fontWeight: 600,
        color: '#333',
    },
    summaryGrandTotal: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 24,
        padding: '8px 12px',
        backgroundColor: '#f5f5f5',
        marginTop: 8,
        borderRadius: 4,
    },
    summaryGrandTotalLabel: {
        fontSize: 16,
        fontWeight: 700,
        color: '#333',
    },
    summaryGrandTotalValue: {
        fontSize: 16,
        fontWeight: 700,
        color: '#333',
    },
    summarySeparator: {
        borderBottom: '1px solid #eee',
        margin: '8px 0',
    },
    footnote: {
        fontSize: 14,
        color: '#888',
        marginTop: 12,
    },
}));

function MeditReview() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { id: caseId } = useParams();
    const location = useLocation();
    const get = useLang('PatientInfo');
    const [contentHeight, setContentHeight] = useState('100svh');
    const [selfDesignData, setSelfDesignData] = useState(null);
    const [caseStatus, setCaseStatus] = useState(null);
    const [treatmentPlan, setTreatmentPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [showReviseDesign, setShowReviseDesign] = useState(false);
    const [showApprove, setShowApprove] = useState(false);
    const [isApproved, setIsApproved] = useState(false);
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryData, setSummaryData] = useState(null);
    const [summaryError, setSummaryError] = useState(null);
    const [approving, setApproving] = useState(false);
    const [videoUrl, setVideoUrl] = useState(null);

    // Parse optional planNo from query string
    const planNo = new URLSearchParams(location.search).get('planNo') || undefined;

    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;
        const prevHtmlOverflow = html.style.overflow;
        const prevBodyOverflow = body.style.overflow;
        const updateContentHeight = () => {
            const navHeight = document.getElementById('navigationBar')?.offsetHeight || 0;
            setContentHeight(`${Math.max(window.innerHeight - navHeight, 0)}px`);
        };

        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
        updateContentHeight();
        const resizeTimer = window.setTimeout(updateContentHeight, 0);
        window.addEventListener('resize', updateContentHeight);

        return () => {
            html.style.overflow = prevHtmlOverflow;
            body.style.overflow = prevBodyOverflow;
            window.clearTimeout(resizeTimer);
            window.removeEventListener('resize', updateContentHeight);
        };
    }, []);

    // Fetch self design data
    useEffect(() => {
        let cancelled = false;
        const fetchSelfDesign = async () => {
            setLoading(true);
            setLoadError(null);
            try {
                const params = planNo ? { planNo } : {};
                const res = await axios.get(`/case/self-design/${caseId}`, { params });
                const { self_design, case_status, treatment_plan, review_video_url } = res.data;
                setSelfDesignData(self_design);
                setCaseStatus(case_status);
                setTreatmentPlan(treatment_plan);
                setIsApproved(!!treatment_plan?.approved_flag);
                setVideoUrl(review_video_url || null);

                // Control button visibility based on case status
                // Approve + Revise Design are relevant when plan is uploaded but not yet approved
                if (case_status === 'PLAN_UPLOADED') {
                    setShowReviseDesign(true);
                    // Show approve button regardless — if already approved, it will be disabled
                    setShowApprove(true);
                } else if (case_status === 'PLAN_APPROVED' || case_status === 'PRODUCTION') {
                    // Plan already approved or in production — hide Revise but keep Approve visible as disabled
                    setShowReviseDesign(false);
                    setShowApprove(true);
                }
            } catch (err) {
                if (cancelled) return;
                const errMsg = err.response?.data?.message || get('case_info.selfDesign.loadingFailed');
                setLoadError(errMsg);
                dispatch(easySnackbar(errMsg, 'error'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchSelfDesign();
        return () => {
            cancelled = true;
        };
    }, [caseId, planNo]);

    // Show modal for critical errors when loading is done and there's an error
    useEffect(() => {
        if (!loading && loadError) {
            const isNoPlan =
                loadError.includes('No treatment plan found') || loadError.includes('Self design record not found');
            if (isNoPlan) {
                const title = loadError.includes('No treatment plan found')
                    ? get('case_info.selfDesign.noTreatmentPlan')
                    : get('case_info.selfDesign.noSelfDesignRecord');
                dispatch(
                    openModal({
                        modalTitle: 'Self Design Data',
                        modalMessage: title,
                        showOk: false,
                    })
                );
            }
        }
    }, [loading, loadError]);

    return (
        <>
            <Backdrop style={{ zIndex: 9999, color: '#fff' }} open={loading}>
                <LoadingSpinner />
            </Backdrop>
            <Backdrop style={{ zIndex: 99999, color: '#fff' }} open={approving}>
                <LoadingSpinner />
            </Backdrop>
            <Box className={classes.root} data-case-id={caseId} style={{ height: contentHeight }}>
                <video
                    className={classes.video}
                    controls
                    src={videoUrl || undefined}
                    aria-label="Medit treatment plan review video"
                />
                <Box className={classes.actions}>
                    {showReviseDesign && (
                        <Button
                            className={classes.actionButton}
                            onClick={async () => {
                                try {
                                    const { data } = await axios.post(`/case/self-design/${caseId}/auth-link`);
                                    if (data.success && data.authorization_url) {
                                        window.open(data.authorization_url, '_blank');
                                    } else {
                                        dispatch(easySnackbar(data.message || 'Failed to generate auth link', 'error'));
                                    }
                                } catch (err) {
                                    dispatch(
                                        easySnackbar(
                                            err.response?.data?.message ||
                                                err.message ||
                                                'Failed to generate auth link',
                                            'error'
                                        )
                                    );
                                }
                            }}
                        >
                            {get('case_info.actionsList.reviseDesignSelfDesign')}
                        </Button>
                    )}
                    {showApprove && (
                        <Button
                            className={classes.actionButton}
                            disabled={isApproved || summaryLoading || approving}
                            style={isApproved ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                            onClick={async () => {
                                if (isApproved) return;
                                setSummaryOpen(true);
                                setSummaryLoading(true);
                                setSummaryError(null);
                                setSummaryData(null);
                                try {
                                    const params = {};
                                    if (planNo) params.planNo = planNo;
                                    if (treatmentPlan?.plan_id) params.planId = treatmentPlan.plan_id;
                                    const res = await axios.get(`/case/self-design/${caseId}/manufacture-summary`, {
                                        params,
                                    });
                                    let summary = res.data.summary;
                                    const upperStages = parseStages(selfDesignData?.upper_stages);
                                    const lowerStages = parseStages(selfDesignData?.lower_stages);
                                    // Skip the stage-0 exclusion adjustment for standalone additional product orders
                                    // (no aligners), where there is no aligner price/count to adjust.
                                    if (
                                        summary.totalAligners > 0 &&
                                        summary.pricePerAligner != null &&
                                        summary.upperAligners === upperStages.length &&
                                        summary.lowerAligners === lowerStages.length
                                    ) {
                                        const upperExcl = upperStages.filter((s) => Number(s) !== 0).length;
                                        const lowerExcl = lowerStages.filter((s) => Number(s) !== 0).length;
                                        const excludedAligners = summary.totalAligners - (upperExcl + lowerExcl);
                                        if (excludedAligners > 0) {
                                            summary = {
                                                ...summary,
                                                upperAligners: upperExcl,
                                                lowerAligners: lowerExcl,
                                                totalAligners: upperExcl + lowerExcl,
                                                totalCostOfAligners: summary.pricePerAligner * (upperExcl + lowerExcl),
                                                grandTotal:
                                                    summary.grandTotal - summary.pricePerAligner * excludedAligners,
                                            };
                                        }
                                    }
                                    setSummaryData(summary);
                                } catch (err) {
                                    const errMsg =
                                        err.response?.data?.message ||
                                        get('case_info.manufactureSummary.loadingFailed');
                                    setSummaryError(errMsg);
                                    dispatch(easySnackbar(errMsg, 'error'));
                                } finally {
                                    setSummaryLoading(false);
                                }
                            }}
                        >
                            {isApproved
                                ? get('case_info.actionsList.approveTreatmentPlanAndManufactureApproved')
                                : get('case_info.actionsList.approveTreatmentPlanAndManufacture')}
                        </Button>
                    )}
                </Box>
            </Box>

            <Dialog open={summaryOpen} onClose={() => !approving && setSummaryOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{get('case_info.manufactureSummary.title')}</DialogTitle>
                <DialogContent>
                    {summaryLoading && (
                        <Box display="flex" justifyContent="center" p={2}>
                            <LoadingSpinner />
                        </Box>
                    )}
                    {summaryError && (
                        <Typography color="error" align="center">
                            {summaryError}
                        </Typography>
                    )}
                    {summaryData && !summaryLoading && !summaryError && (
                        <>
                            {/* Aligners (MOS1100A) - omitted for standalone additional product orders */}
                            {summaryData.totalAligners > 0 && (
                                <>
                                    <div className={classes.summaryRow}>
                                        <span className={classes.summaryLabel}>
                                            {get('case_info.manufactureSummary.totalManufacturingSteps')}
                                        </span>
                                        <span className={classes.summaryValue}>
                                            {summaryData.totalManufacturingSteps}{' '}
                                            {get('case_info.manufactureSummary.totalManufacturingStepsUnit')}
                                        </span>
                                    </div>
                                    <div className={classes.summaryRow}>
                                        <span className={classes.summaryLabel}>
                                            {get('case_info.manufactureSummary.pricePerAligner')}
                                        </span>
                                        <span className={classes.summaryValue}>
                                            {summaryData.currency}{' '}
                                            {summaryData.pricePerAligner?.toLocaleString() || 'N/A'}
                                        </span>
                                    </div>
                                    <div className={classes.summaryRow}>
                                        <span className={classes.summaryLabel}>
                                            {get('case_info.manufactureSummary.totalAligners')}
                                        </span>
                                        <span className={classes.summaryValue}>
                                            {summaryData.totalAligners}{' '}
                                            {get('case_info.manufactureSummary.alignersUnit')}
                                            {' ('}
                                            {summaryData.upperAligners} {get('case_info.manufactureSummary.upperLabel')}
                                            {' + '}
                                            {summaryData.lowerAligners} {get('case_info.manufactureSummary.lowerLabel')}
                                            {')'}
                                        </span>
                                    </div>
                                    <div className={classes.summaryRow}>
                                        <span className={classes.summaryLabel}>
                                            {get('case_info.manufactureSummary.totalCostOfAligners')}
                                        </span>
                                        <span className={classes.summaryValue}>
                                            {summaryData.currency}{' '}
                                            {(summaryData.totalCostOfAligners || 0).toLocaleString()}
                                        </span>
                                    </div>
                                </>
                            )}

                            {/* Passive Aligners (MOS1100PA) */}
                            {summaryData.passiveAlignersPresent && (
                                <>
                                    <div className={classes.summaryRow}>
                                        <span className={classes.summaryLabel}>
                                            {get('case_info.manufactureSummary.pricePerPassiveAligner')}
                                        </span>
                                        <span className={classes.summaryValue}>
                                            {summaryData.currency}{' '}
                                            {summaryData.pricePerPassiveAligner?.toLocaleString() || 'N/A'}
                                        </span>
                                    </div>
                                    <div className={classes.summaryRow}>
                                        <span className={classes.summaryLabel}>
                                            {get('case_info.manufactureSummary.totalPassiveAligners')}
                                        </span>
                                        <span className={classes.summaryValue}>
                                            {summaryData.totalPassiveAligners}{' '}
                                            {get('case_info.manufactureSummary.alignersUnit')}
                                            {' ('}
                                            {summaryData.passiveUpper} {get('case_info.manufactureSummary.upperLabel')}
                                            {' + '}
                                            {summaryData.passiveLower} {get('case_info.manufactureSummary.lowerLabel')}
                                            {')'}
                                        </span>
                                    </div>
                                    <div className={classes.summaryRow}>
                                        <span className={classes.summaryLabel}>
                                            {get('case_info.manufactureSummary.totalCostOfPassiveAligners')}
                                        </span>
                                        <span className={classes.summaryValue}>
                                            {summaryData.currency}{' '}
                                            {summaryData.totalCostOfPassiveAligners.toLocaleString()}
                                        </span>
                                    </div>
                                </>
                            )}

                            {/* Attachment Templates (MOS1100T) */}
                            {summaryData.attachmentTemplatesPresent && (
                                <>
                                    <div className={classes.summaryRow}>
                                        <span className={classes.summaryLabel}>
                                            {get('case_info.manufactureSummary.pricePerAttachmentTemplate')}
                                        </span>
                                        <span className={classes.summaryValue}>
                                            {summaryData.currency}{' '}
                                            {summaryData.pricePerAttachmentTemplate?.toLocaleString() || 'N/A'}
                                        </span>
                                    </div>
                                    <div className={classes.summaryRow}>
                                        <span className={classes.summaryLabel}>
                                            {get('case_info.manufactureSummary.totalAttachmentTemplates')}
                                        </span>
                                        <span className={classes.summaryValue}>
                                            {summaryData.totalAttachmentTemplates}{' '}
                                            {get('case_info.manufactureSummary.alignersUnit')}
                                            {' ('}
                                            {summaryData.attachmentUpper}{' '}
                                            {get('case_info.manufactureSummary.upperLabel')}
                                            {' + '}
                                            {summaryData.attachmentLower}{' '}
                                            {get('case_info.manufactureSummary.lowerLabel')}
                                            {')'}
                                        </span>
                                    </div>
                                    <div className={classes.summaryRow}>
                                        <span className={classes.summaryLabel}>
                                            {get('case_info.manufactureSummary.totalCostOfAttachmentTemplates')}
                                        </span>
                                        <span className={classes.summaryValue}>
                                            {summaryData.currency}{' '}
                                            {summaryData.totalCostOfAttachmentTemplates.toLocaleString()}
                                        </span>
                                    </div>
                                </>
                            )}

                            {/* Retainers (MOS3021) */}
                            {summaryData.retainersPresent && (
                                <>
                                    <div className={classes.summaryRow}>
                                        <span className={classes.summaryLabel}>
                                            {get('case_info.manufactureSummary.pricePerRetainer')}
                                        </span>
                                        <span className={classes.summaryValue}>
                                            {summaryData.currency}{' '}
                                            {summaryData.pricePerRetainer?.toLocaleString() || 'N/A'}
                                        </span>
                                    </div>
                                    <div className={classes.summaryRow}>
                                        <span className={classes.summaryLabel}>
                                            {get('case_info.manufactureSummary.totalRetainers')}
                                        </span>
                                        <span className={classes.summaryValue}>
                                            {summaryData.totalRetainers}{' '}
                                            {get('case_info.manufactureSummary.alignersUnit')}
                                            {' ('}
                                            {summaryData.retainerUpper} {get('case_info.manufactureSummary.upperLabel')}
                                            {' + '}
                                            {summaryData.retainerLower} {get('case_info.manufactureSummary.lowerLabel')}
                                            {')'}
                                        </span>
                                    </div>
                                    <div className={classes.summaryRow}>
                                        <span className={classes.summaryLabel}>
                                            {get('case_info.manufactureSummary.totalCostOfRetainers')}
                                        </span>
                                        <span className={classes.summaryValue}>
                                            {summaryData.currency} {summaryData.totalCostOfRetainers.toLocaleString()}
                                        </span>
                                    </div>
                                </>
                            )}
                            <div className={classes.summarySeparator} />
                            <div className={classes.summaryRow}>
                                <span className={classes.summaryLabel}>
                                    {get('case_info.manufactureSummary.shippingAndHandling')}
                                </span>
                                <span className={classes.summaryValue}>
                                    {get('case_info.manufactureSummary.expressCourier')}
                                </span>
                            </div>
                            <div className={classes.summaryRow}>
                                <span className={classes.summaryLabel}>
                                    {get('case_info.manufactureSummary.totalShipmentFee')}
                                </span>
                                <span className={classes.summaryValue}>
                                    {summaryData.currency} {summaryData.shipmentFee.toLocaleString()}
                                </span>
                            </div>
                            <div className={classes.summaryGrandTotal}>
                                <span className={classes.summaryGrandTotalLabel}>
                                    {get('case_info.manufactureSummary.grandTotal')}:
                                </span>
                                <span className={classes.summaryGrandTotalValue}>
                                    {summaryData.currency} {summaryData.grandTotal.toLocaleString()}
                                </span>
                            </div>
                            <Typography className={classes.footnote}>
                                {get('case_info.manufactureSummary.footnote')}
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSummaryOpen(false)} disabled={approving} style={{ color: '#000' }}>
                        {get('case_info.manufactureSummary.cancel')}
                    </Button>
                    <Button
                        onClick={async () => {
                            if (!treatmentPlan?.plan_id) return;
                            setApproving(true);
                            try {
                                await axios.post(`/case/self-design/${caseId}/${treatmentPlan.plan_id}/approve`);
                                dispatch(easySnackbar(get('case_info.manufactureSummary.approveSuccess'), 'success'));
                                setSummaryOpen(false);
                                setIsApproved(true);
                                setShowReviseDesign(false);
                            } catch (err) {
                                const errMsg =
                                    err.response?.data?.message || get('case_info.manufactureSummary.approveFailed');
                                dispatch(easySnackbar(errMsg, 'error'));
                            } finally {
                                setApproving(false);
                            }
                        }}
                        disabled={summaryLoading || !!summaryError || approving || !summaryData}
                        style={{ backgroundColor: localColorTheme.purple, color: '#fff' }}
                        variant="contained"
                    >
                        {get('case_info.manufactureSummary.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default withRouter(MeditReview);
